import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { translations } from '../translations';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import { useUserData } from '../contexts/UserDataContext';

interface LoginProps {
    onSwitchToSignup: () => void;
    onClose: () => void;
    selectedLanguage?: string;
}

export default function Login({ onSwitchToSignup, onClose, selectedLanguage }: LoginProps) {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const { login, currentUser } = useAuth();
    const { refreshUserData, userData } = useUserData();
    const actualSelectedLanguage = selectedLanguage || userData?.selectedLanguage || 'en';
    const lang = (actualSelectedLanguage in translations ? actualSelectedLanguage : 'en') as keyof typeof translations;
    const t = translations[lang] || translations.en;

    // Note: PublicRoute component will handle redirects automatically
    // No need for manual navigation here

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();

        try {
            setError('');
            setSuccessMessage('');
            setLoading(true);
            console.log('Attempting login with email:', email);
            await login(email, password);
            console.log('Login successful, auth state will update automatically');
            setSuccessMessage('Login successful! Redirecting to your dashboard...');
            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (error: any) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }

    const handleGoogleSignIn = async () => {
        try {
            setLoading(true);
            setError('');
            setSuccessMessage('');

            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;
            const userId = user.uid;

            console.log('Google sign-in successful for userId:', userId);

            // Create user in backend with default English language
            const createResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://english-guru-backend.onrender.com'}/api/user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': process.env.REACT_APP_API_KEY || 'development-api-key',
                },
                body: JSON.stringify({
                    userId: userId,
                    selectedLanguage: 'en'
                })
            });

            if (createResponse.ok) {
                // Refresh user data to load the newly created user
                await refreshUserData();
                console.log('Google sign-in successful, auth state will update automatically');
                setSuccessMessage('Welcome to your English learning journey...');
                setTimeout(() => {
                    window.location.href = '/home';
                }, 2000);
            } else {
                const errorData = await createResponse.json();
                console.log('Google sign-in failed:', errorData);
                // If user already exists, that's fine - just proceed
                if (createResponse.status === 400 && errorData.error?.includes('already exists')) {
                    setSuccessMessage('Welcome back! Redirecting to your dashboard...');
                    setTimeout(() => {
                        window.location.href = '/home';
                    }, 1500);
                } else {
                    setError('Failed to create user account. Please try again.');
                }
            }
        } catch (error: any) {
            console.error('Google sign-in error:', error);
            setError(error.message || 'Failed to sign in with Google');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gradient-to-br from-blue-900/50 via-purple-900/50 to-pink-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 min-h-screen">
            <div className="bg-white/90 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 w-full max-w-sm sm:max-w-md mx-4 relative overflow-hidden max-h-[90vh] overflow-y-auto">
                {/* Decorative background elements */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full opacity-20 blur-2xl"></div>
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-br from-pink-400 to-blue-500 rounded-full opacity-20 blur-2xl"></div>

                {/* Header */}
                <div className="relative z-10 p-4 sm:p-6 md:p-8">
                    <div className="text-center mb-6 sm:mb-8">
                        <div className="text-4xl sm:text-5xl mb-3 sm:mb-4 animate-bounce-slow">🔐</div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold bg-gradient-to-r from-blue-600 via-purple-500 to-pink-500 bg-clip-text text-transparent mb-2">
                            {t.landing?.login || 'Welcome Back'}
                        </h2>
                        <p className="text-gray-600 text-base sm:text-lg">
                            {'Sign in to continue your English learning journey'}
                        </p>
                    </div>

                    {successMessage && (
                        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6">
                            <div className="flex items-center mb-2">
                                <span className="text-green-500 mr-2">✅</span>
                                <span className="font-semibold">Success!</span>
                            </div>
                            <p className="text-sm">{successMessage}</p>
                        </div>
                    )}

                    <button
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 rounded-xl py-3 mb-6 shadow hover:bg-gray-50 transition-all font-semibold text-gray-700 text-sm sm:text-base disabled:opacity-50"
                        style={{ boxShadow: '0 2px 8px 0 rgba(66,133,244,0.08)' }}
                    >
                        <img src="/google-icon.svg" alt="Google" style={{ width: 20, height: 20 }} className="sm:w-6 sm:h-6" />
                        <span>Sign in with Google</span>
                    </button>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
                            <div className="flex items-center mb-2">
                                <span className="text-red-500 mr-2">⚠️</span>
                                <span className="font-semibold">Login Error</span>
                            </div>
                            <p className="text-sm">{error}</p>
                            <button
                                onClick={() => setError('')}
                                className="text-red-600 hover:text-red-800 text-sm font-semibold mt-2"
                            >
                                Dismiss
                            </button>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                        <div>
                            <label className="block text-gray-700 text-sm font-semibold mb-2 sm:mb-3 flex items-center">
                                <span className="mr-2">📧</span>
                                {t.landing?.email || 'Email Address'}
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm transition-all duration-200 text-sm sm:text-base"
                                placeholder={t.landing?.email || 'Enter your email'}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-semibold mb-2 sm:mb-3 flex items-center">
                                <span className="mr-2">🔒</span>
                                {t.landing?.password || 'Password'}
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm transition-all duration-200 text-sm sm:text-base"
                                placeholder={t.landing?.password || 'Enter your password'}
                                required
                            />
                            <div className="text-right mt-2">
                                <button
                                    type="button"
                                    className="text-blue-600 hover:underline text-xs sm:text-sm font-semibold focus:outline-none"
                                    onClick={() => navigate('/forgot-password')}
                                >
                                    Forgot password?
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full sm:flex-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2 sm:py-3 px-4 sm:px-6 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-600 disabled:opacity-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 text-sm sm:text-base"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center">
                                        <span className="animate-spin mr-2">⏳</span>
                                        {'Signing in...'}
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center">
                                        <span className="mr-2">🚀</span>
                                        {t.landing?.login || 'Sign In'}
                                    </span>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="w-full sm:flex-1 bg-gray-100 text-gray-700 py-2 sm:py-3 px-4 sm:px-6 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200 text-sm sm:text-base"
                            >
                                {'Cancel'}
                            </button>
                        </div>
                    </form>

                    <div className="mt-6 sm:mt-8 text-center">
                        <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base">
                            {"Don't have an account yet?"}
                        </p>
                        <button
                            onClick={onSwitchToSignup}
                            className="text-blue-600 hover:text-blue-800 font-semibold text-base sm:text-lg transition-colors duration-200 hover:scale-105 inline-flex items-center"
                        >
                            <span className="mr-2">✨</span>
                            {t.landing?.signup || 'Create Account'}
                        </button>
                    </div>
                </div>

                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold focus:outline-none transition-colors z-20"
                    aria-label="Close"
                >
                    ×
                </button>
            </div>
        </div>
    );
} 